<template>
  <v-container fluid>
    <v-card elevation="0">
      <v-card-text class="pa-0 ma-0">
        <!-- Top row with Waves and Wind Overlays -->
        <v-row justify="center" class="wave-wind-row">
          <v-col cols="6" class="text-center pb-3">
            <waves-overlay
              :site="site"
              @latestDate="setTimestamp"
              @error="handleError"
              class="overlay-component"
            ></waves-overlay>
          </v-col>
          <v-col cols="6" class="text-center pb-3">
            <wind-overlay
              :site="site"
              @latestDate="setTimestamp"
              class="overlay-component"
            ></wind-overlay>
          </v-col>
        </v-row>

        <!-- New row with Water Temperature and Barometer -->
        <v-row justify="center" class="temp-baro-row">
          <v-col cols="6" class="text-center pb-3">
            <water-temperature
              :site="site"
              @latestDate="setTimestamp"
              class="overlay-component"
            ></water-temperature>
          </v-col>
          <v-col cols="6" class="text-center pb-3">
            <barometer-view
              :site="site"
              @latestDate="setTimestamp"
              class="overlay-component"
            ></barometer-view>
          </v-col>
        </v-row>

        <!-- Latest update timestamp -->
        <v-row justify="center" v-if="timestamp">
          <v-col cols="12">
            <v-row class="text-center">
              <v-col cols="6" class="mx-auto">
                Latest update:
                <b class="mr-1">{{
                  new Date(timestamp).toLocaleDateString("en-NZ")
                }}</b>
                <b>{{
                  new Date(timestamp)
                    .toLocaleTimeString("en-NZ", { hour12: false })
                    .slice(0, -3)
                }}</b>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <!-- Error message -->
        <v-alert type="info" v-if="errored">
          This site is currently not available.
        </v-alert>

        <!-- BuoyMap component for displaying the map -->
        <div v-if="!loading && !errored">
          <v-row justify="center" class="map-row">
            <v-col cols="12" md="8" lg="8" class="map-col">
              <buoy-map
                :site="site"
                :coordinates="currentCoordinates"
                :zoom="initialZoom"
              ></buoy-map>
            </v-col>
          </v-row>
        </div>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import WavesOverlay from "@/components/wavesOverlay.vue";
import WindOverlay from "@/components/windOverlay.vue";
import WaterTemperature from "@/components/waterTemperature.vue";
import BarometerView from "@/components/BarometerView.vue";
import BuoyMap from "@/components/BuoyMap.vue";
import { fetchSiteCoordinates } from "@/Service/fetchMetadata.js";

export default {
  name: "CurrentConditions",
  components: {
    WavesOverlay,
    WindOverlay,
    WaterTemperature,
    BarometerView,
    BuoyMap,
  },
  data() {
    return {
      timestamp: null,
      errored: false,
      loading: true, // Loading state for the map
      initialZoom: 10, // Adjust this value to change the map zoom level
      mapKey: 0, // Key to force map re-render when site changes
      currentCoordinates: [0, 0], // Default coordinates
      defaultSite: "Tasman Bay", // Default site name
    };
  },
  computed: {
    site() {
      return this.$store.state.site || this.defaultSite; // Use default site if no site is set in the store
    },
  },
  watch: {
    site: {
      immediate: true,
      handler() {
        this.fetchSiteCoordinates(); // Fetch coordinates whenever the site changes
        this.timestamp = null;
        this.loading = true; // Set loading to true while fetching new coordinates
        this.mapKey += 1; // Update the map key to force re-render
      },
    },
  },
  methods: {
    async fetchSiteCoordinates() {
      try {
        const { latitude, longitude } = await fetchSiteCoordinates(this.site);

        if (latitude !== null && longitude !== null) {
          this.currentCoordinates = [latitude, longitude];
          this.loading = false;
          this.errored = false;
        } else {
          // Handle case where coordinates are not available
          this.errored = true;
          this.loading = false;
          console.warn("No coordinates available for site:", this.site);
        }
      } catch (error) {
        this.errored = true;
        this.loading = false;
        console.error("Error fetching coordinates:", error);
      }
    },
    setTimestamp(pl) {
      if (pl > this.timestamp) {
        this.timestamp = pl;
      }
    },
    handleError(message) {
      this.errored = true;
      console.error("Overlay error:", message);
    },
  },
};
</script>

<style scoped>
.temp-baro-row {
  margin-right: 4rem;
  margin-left: 4rem;
}

.wave-wind-row {
  margin-top: 2rem;
  margin-right: 4rem;
  margin-left: 4rem;
}

.site-link-wrapper {
  font-size: 18px;
  color: #00549e; /* Vuetify's primary color */
  font-weight: bold;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
}

.coordinates-link {
  color: #00549e;
  text-decoration: none;
  margin-left: 4px;
}

.coordinates-link:hover {
  text-decoration: underline;
}

.map-row {
  margin-top: 16px;
}

.map-col {
  height: 300px; /* Default height */
  border: 1px solid #e0e0e0;
  margin-bottom: 2rem;
  border-radius: 10px;
}

/* Increase height on larger screens */
@media (min-width: 1280px) {
  .map-col {
    height: 500px;
  }
}

/* Decrease height on small screens */
@media (max-width: 600px) {
  .site-link-wrapper {
    font-size: 16px;
  }
  .map-col {
    height: 200px;
  }
  .temp-baro-row {
    margin-right: 0rem;
    margin-left: 0rem;
  }

  .wave-wind-row {
    margin-right: 0rem;
    margin-left: 0rem;
  }

  .v-container {
    padding-left: 0px;
    padding-right: 0px;
  }
}

/* Style for the popup content */
.popup-content {
  background-color: white;
  color: #1976d2; /* Vuetify's primary color */
  padding: 5px;
}

/* Override Leaflet's default popup styles */
.leaflet-popup-content-wrapper {
  background-color: white !important;
}

.leaflet-popup-content {
  margin: 0;
}

.leaflet-popup-tip {
  background: white !important;
}
</style>
